import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { graphQueryBang } from "shared/lib/graphql"

import SearchInput from "./search_input"
import Spinner from "shared/components/spinner"
import { trackClickServiceSelected } from "./utils/telemetry"
import { partition, pluralizeStepType } from "shared/lib/utils"
import classnames from "classnames"

import "./service_selector.scss"

export default function ServiceSelector({
  loading,
  onServiceSelect,
  onServiceSelectQuery,
  services,
  stepType,
  updateLoading,
}) {
  const [matchingServices, updateMatchingServices] = useState([])
  const [unavailableServices, updateUnavailableServices] = useState([])

  function updateServices(services) {
    const [matching, unavailable] = partition(
      services,
      service => service[`public_${pluralizeStepType(stepType)}`].length > 0
    )

    updateMatchingServices(matching)
    updateUnavailableServices(unavailable)
  }

  useEffect(() => {
    updateServices(services)
  }, [services])

  const onSearchResult = serviceIds => {
    const visibleServices = services
      .filter(s => serviceIds.includes(s.id))
      .sort(
        (first, second) =>
          serviceIds.indexOf(first.id) - serviceIds.indexOf(second.id)
      )
    updateServices(visibleServices)
  }

  const onClick = service => {
    if (loading) return null

    updateLoading(true)

    trackClickServiceSelected({
      serviceModuleName: service.module_name,
      stepType,
    })

    graphQueryBang(onServiceSelectQuery, {
      serviceModuleName: service.module_name,
    })
      .then(data => {
        onServiceSelect({ ...service, ...data.channel })
      })
      .catch(() => updateLoading(false))
  }

  const tiles = (services, linkable = true) =>
    services.map(service => {
      let detail = (
        <div>
          <img src={service.image} alt={service.name} title={service.name} />
          <span>{service.name}</span>
        </div>
      )
      if (linkable) {
        detail = (
          <a
            title={`Choose service ${service.name}`}
            onClick={() => onClick(service)}
          >
            {detail}
          </a>
        )
      } else {
        detail = (
          <span
            title={`${
              service.name
            } doesn't have any available ${pluralizeStepType(stepType)}`}
          >
            {detail}
          </span>
        )
      }
      return (
        <li
          key={service.module_name}
          styleName={classnames("service-tile", {
            "disabled-service-tile": !linkable,
          })}
          style={{ backgroundColor: service.brand_color }}
          role="listitem"
        >
          {detail}
        </li>
      )
    })

  return (
    <div styleName="service-selector">
      <>
        {loading || !services.length ? (
          <Spinner />
        ) : (
          <>
            <SearchInput services={services} onSearch={onSearchResult} />
            <ul styleName="service-grid" role="list">
              {tiles(matchingServices)}
            </ul>
            {unavailableServices.length > 0 && (
              <>
                <h4 styleName="service-grid-title">
                  Services with no available {pluralizeStepType(stepType)}
                </h4>
                <ul styleName="service-grid" role="list">
                  {tiles(unavailableServices, false)}
                </ul>
              </>
            )}
          </>
        )}
      </>
    </div>
  )
}

ServiceSelector.propTypes = {
  loading: PropTypes.bool.isRequired,
  onServiceSelect: PropTypes.func.isRequired,
  onServiceSelectQuery: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      module_name: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string,
      brand_color: PropTypes.string,
    })
  ).isRequired,
  stepType: PropTypes.oneOf(["trigger", "query", "action"]).isRequired,
  updateLoading: PropTypes.func.isRequired,
}
